<template>
  <div v-if="loading" class="location-results location-results--scroll">
    <div v-for="n in 3" :key="n" class="location-results__skeleton-row">
      <div
        class="location-results__skeleton-text"
        :style="{ width: skeletonWidths[n - 1] + 'px' }"
      />
    </div>
  </div>

  <div
    v-else-if="items.length"
    class="location-results location-results--scroll"
    role="listbox"
  >
    <div
      v-for="item in items"
      :key="item.id"
      class="location-results__row-wrap"
      role="presentation"
    >
      <div
        class="location-results__row"
        :class="{ 'location-results__row--selected': isSelected(item) }"
        role="option"
        tabindex="0"
        :aria-selected="isSelected(item) ? 'true' : 'false'"
        @click.stop="selectItem(item)"
        @keydown.enter.prevent="selectItem(item)"
        @keydown.space.prevent="selectItem(item)"
      >
        <span class="location-results__label">{{ formatLabel(item) }}</span>
        <v-icon
          v-if="isSelected(item)"
          class="location-results__check"
          aria-hidden="true"
        >
          mdi-check
        </v-icon>
      </div>
    </div>
  </div>

  <div v-else class="location-results location-results__empty">
    <p class="location-results__empty-title">No matches</p>
    <p class="location-results__empty-sub">
      {{ hasQuery ? "Try a different search" : emptyMessage }}
    </p>
  </div>
</template>

<script setup>
const props = defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  hasQuery: { type: Boolean, default: false },
  emptyMessage: { type: String, default: "No options available" },
  selectedId: { type: [String, Number], default: null },
  formatLabel: {
    type: Function,
    default: (item) => item?.name || "",
  },
});

const emit = defineEmits(["select"]);

const skeletonWidths = [220, 180, 200];

function isSelected(item) {
  return props.selectedId != null && item?.id === props.selectedId;
}

function selectItem(item) {
  emit("select", item);
}
</script>

<style scoped>
.location-results {
  font-family: Inter, sans-serif;
  padding: 4px;
  box-sizing: border-box;
}

.location-results--scroll {
  max-height: 252px;
  overflow-y: auto;
}

.location-results__row-wrap {
  padding: 1px 6px;
}

.location-results__row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 10px 10px 8px;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  box-sizing: border-box;
}

.location-results__row--selected {
  background: #f9fafb;
}

.location-results__row:hover {
  background: #f9fafb;
}

.location-results__label {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.location-results__check {
  flex-shrink: 0;
  font-size: 20px !important;
  color: #6941c6 !important;
}

.location-results__skeleton-row {
  padding: 10px 14px;
  height: 44px;
  box-sizing: border-box;
}

.location-results__skeleton-text {
  height: 12px;
  border-radius: 999px;
  background: #eaecf0;
}

.location-results__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  gap: 4px;
}

.location-results__empty-title {
  font-size: 14px;
  font-weight: 500;
  color: #475467;
  margin: 0;
}

.location-results__empty-sub {
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  margin: 0;
}
</style>
